import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { cn } from 'lib/utils';
import { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'store';
import { saveFile, saveLogoInfo, setFileName } from 'store/slices/logoGen';
import z from 'zod';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Button } from 'components/ui/button';
import { LoaderIcon } from 'lucide-react';
import ApplyChangesDialog from './modals/ApplyChangesDialog';

import { LOGO_STYLES, INDUSTRIES, TONES, COLORS, colorMap } from '../constants/data';
import { LogoGenService } from '../services/logo-gen.api';
import FormField from './FormField';
import RegenerateModal from './modals/RegenerateModal';

const schema = z.object({
  company_name: z.string().nonempty({ message: 'Please enter a company name' }),
  company_description: z.string().min(5, {
    message: 'Company description must be at least 5 characters',
  }),
  style: z.string().nonempty({ message: 'Please select a style' }),
  industry: z.string().nonempty({ message: 'Please select an industry' }),
  tone: z.string().nonempty({ message: 'Please select a tone of voice' }),
  color: z.string().nonempty({ message: 'Please select a color' }),
  negative_prompt: z.string().optional(),
  token: z.string().optional(),
});

type TSchema = z.infer<typeof schema>;

export default function EditLogoForm() {
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    isSaved,
    companyName,
    companyDescription,
    style,
    industry,
    tone,
    color,
    negativePrompt,
  } = useSelector((state: RootState) => state.logoGen);

  const { mutate, isPending } = useMutation({
    mutationKey: ['generate-logo-from-text'],
    mutationFn: LogoGenService.generateLogoFromText,
    onSuccess: (data) => {
      dispatch(
        saveLogoInfo({
          tempLogoUrl: data.data.logo,
          style: form.getValues('style'),
          companyName: form.getValues('company_name'),
          companyDescription: form.getValues('company_description'),
          industry: form.getValues('industry'),
          tone: form.getValues('tone'),
          color: form.getValues('color'),
          negativePrompt: form.getValues('negative_prompt') || '',
        })
      );
      dispatch(setFileName({ fileName: 'Untitled 1' }));
      dispatch(saveFile({ fileSaved: false }));
      navigate(`/text-to-logo/result`);
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const form = useForm<TSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      company_name: companyName,
      company_description: companyDescription,
      style: style,
      industry,
      tone,
      color,
      negative_prompt: negativePrompt,
    },
  });

  const { formState: { errors }, setValue, watch } = form;
  const selectedColor = watch('color');

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }

    const token = await executeRecaptcha('KaiRecaptcha0911');
    form.setValue('token', token);
  }, [executeRecaptcha]);

  const handleContinueWithoutSaving = async () => {
    setIsAlertDialogOpen(false);
    await handleReCaptchaVerify();
    mutate({
      company_name: form.getValues('company_name'),
      company_description: form.getValues('company_description'),
      style: form.getValues('style'),
      industry: form.getValues('industry'),
      tone: form.getValues('tone'),
      color: form.getValues('color'),
      negative_prompt: "", // no negative promt required
      token: form.getValues('token'),
    });
  };

  const onSubmit = async (values: TSchema) => {
    await handleReCaptchaVerify();
    mutate({
      ...values,
    });
  };

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  return (
    <div className="bg-background-main text-text-primary flex flex-col rounded-md p-3 px-4 col-span-3 overflow-y-auto">
      <p className="hidden lg:block text-primary uppercase text-sm">
        Edit Logo
      </p>

      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mt-3 flex flex-1 flex-col gap-4"
        >
          <div className="flex flex-col gap-4">
            <FormField
              name="company_name"
              label="Company name"
              placeholder="Enter company name"
              error={errors.company_name}
            />
            <FormField
              name="company_description"
              label="Company description"
              placeholder="Enter description"
              error={errors.company_description}
            />

            <div className="hidden lg:block">
              <p className="uppercase text-xs">
                Don&apos;t like this logo?
              </p>
              <RegenerateModal />
            </div>

            <div className="border-t border-gray-300"></div>

            {/* Industry Dropdown */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Industry</label>
              <Select 
                defaultValue={industry}
                onValueChange={(value) => setValue('industry', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map(industry => (
                    <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.industry && (
                <p className="text-red-500 text-xs">{errors.industry.message}</p>
              )}
            </div>

            <div className="flex gap-4 w-full">
              {/* Tone Dropdown */}
              <div className="flex-1 space-y-2">
                <label className="text-sm font-medium">Tone</label>
                <Select 
                  defaultValue={tone}
                  onValueChange={(value) => setValue('tone', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {TONES.map(tone => (
                      <SelectItem key={tone} value={tone}>
                        {tone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.tone && (
                  <p className="text-red-500 text-xs">{errors.tone.message}</p>
                )}
              </div>

              {/* Color Dropdown */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Colour</label>
                <Select onValueChange={(value) => setValue('color', value)}>
                  <SelectTrigger className="w-full px-2 flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded border border-gray-200 flex-shrink-0"
                      style={{
                        backgroundColor: colorMap[selectedColor],
                        borderColor: selectedColor === 'White' ? '#e5e7eb' : undefined
                      }}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {COLORS.map((color) => (
                      <SelectItem 
                        key={color} 
                        value={color}
                        className="flex items-center gap-2"
                      >
                        <div
                          className="w-4 h-4 rounded border border-gray-200 flex-shrink-0"
                          style={{
                            backgroundColor: colorMap[color],
                            borderColor: color === 'White' ? '#e5e7eb' : undefined
                          }}
                        />
                        <span>{color}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.color && (
                  <p className="text-red-500 text-xs">{errors.color.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm">Select style</label>
            <Controller
              control={form.control}
              name="style"
              render={({ field }) => {
                return (
                  <div className="lg:overflow-y-scroll lg:h-[35vh] overflow-x-auto lg:overflow-x-hidden">
                    <div className="lg:grid lg:grid-cols-2 lg:gap-4 flex gap-2 pb-2 lg:pb-0">
                      {LOGO_STYLES.map((style) => (
                        <button
                          key={style.id}
                          type="button"
                          className={cn(
                            'lg:w-full flex-none w-32 rounded-md overflow-hidden border border-[#D7DAD9] bg-background',
                            field.value == style.id && 'border-primary'
                          )}
                          onClick={() => {
                            field.onChange(style.id);
                          }}
                        >
                          <div className="w-full lg:h-32 h-24 flex items-center justify-center">
                            <img
                              src={style.imagePath}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="p-2 text-xs">{style.text}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }}
            />
          </div>

          <div className="w-full mt-auto">
            <Button
              disabled={!form.formState.isDirty || isPending}
              className="w-full text-lg"
              variant={'customPrimary'}
              onClick={isSaved ? handleContinueWithoutSaving : () => setIsAlertDialogOpen(true)}
              type="button"
            >
              {isPending && (
                <div className="animate-spin">
                  <LoaderIcon size={20} />
                </div>
              )}
              {isPending ? 'Applying' : 'Apply'}
            </Button>

            {!isSaved && (
              <ApplyChangesDialog
                isOpen={isAlertDialogOpen}
                setIsOpen={setIsAlertDialogOpen}
                isPending={isPending}
                handleContinueWithoutSaving={handleContinueWithoutSaving}
                formState={form.formState}
              />
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
}